import { Component } from '@theme/component';

/**
 * ROTAS69 Wishlist
 *
 * Two-layer persistence:
 *  - Guests: stored in `localStorage`, so the wishlist works with no account and no backend.
 *  - Logged-in customers: слияние с серверным списком через подписанный App Proxy
 *    (`/apps/r69/sync` -> воркер -> KV), чтобы список ходил за человеком между устройствами.
 *
 * The store is the single source of truth on the client. UI elements subscribe to the
 * `wishlist:change` event dispatched on `document` and never read storage directly.
 *
 * Ported from the FAORIA wishlist (Horizon 3.5.1) to ROTAS69 (Horizon 4.1.1). Brand-neutral
 * behaviour; all shopper-facing strings are localized and passed in from Liquid via data-attrs.
 */

const STORAGE_KEY = 'r69:wishlist';
const CHANGE_EVENT = 'wishlist:change';
// Fired after this element swaps in a freshly rendered section, so the section's batch
// loader can start over on the new markup (it holds no reference to the old nodes).
const RENDERED_EVENT = 'wishlist:rendered';
// Подписанный App Proxy. Корень /apps/r69 воркер намеренно отдаёт пустым 404 — список живёт
// на /sync. Значение по умолчанию для настройки wishlist_proxy_path лежит в settings_schema.json
// и должно совпадать с этой строкой.
const DEFAULT_ENDPOINT = '/apps/r69/sync';
// Снимок серверного списка на момент последней успешной синхронизации: без него слияние не
// умеет отличить «товар добавили на другом устройстве» от «товар здесь удалили».
const BASE_KEY = 'r69:wl-base';
// Чей список лежит в этом браузере: 'c:<id>' у вошедшего. Гость метки не ставит.
const LIST_OWNER_KEY = 'r69:wl-owner';
// Тот же потолок, что режет воркер (MAX_ITEMS в sanitizeItems): без него список длиннее
// потолка расходится с серверным навсегда и шлёт POST на каждой загрузке страницы.
const MAX_ITEMS = 250;

/**
 * Снимок последней синхронизации: `{rev, items}`; null — на этом устройстве её ещё не было.
 * `rev` — монотонная ревизия, которую выдаёт воркер на каждую запись. Она нужна потому, что KV
 * согласован лишь в конечном счёте: чтение сразу после записи может вернуть ПРЕДЫДУЩЕЕ состояние,
 * и без ревизии только что добавленный товар выглядел бы как удалённый на другом устройстве.
 */
function readBase() {
  try {
    const raw = localStorage.getItem(BASE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return { rev: 0, items: parsed };            // формат до ревизий
    if (parsed && Array.isArray(parsed.items)) return { rev: Number(parsed.rev) || 0, items: parsed.items };
    return null;
  } catch (error) {
    return null;
  }
}

function writeBase(rev, items) {
  // У гостя базы быть не должно: он ни с чем не синхронизируется, а оставленная база заставила бы
  // следующее слияние счесть его товары «удалёнными на сервере».
  const cfg = window.R69 && window.R69.wishlist;
  if (!cfg || !cfg.loggedIn || !cfg.syncEnabled) return;
  try {
    localStorage.setItem(BASE_KEY, JSON.stringify({ rev: Number(rev) || 0, items }));
  } catch (error) {
    /* приватный режим — обойдёмся объединением */
  }
}

/**
 * Отпечаток списка для сравнения «наше против серверного». Порядок игнорируется намеренно: два
 * устройства держат список в своём порядке, и сравнение строкой заставляло бы их бесконечно
 * переписывать друг друга. Хендл игнорируется тоже — он свой на каждой витрине (.lv/.lt/.ee),
 * товар же один и тот же.
 */
function listKey(items) {
  return items.map((i) => i.id + '|' + (i.variantId || '')).sort().join(',');
}

/**
 * Трёхстороннее слияние списков по общей базе.
 *
 * Простое объединение серверного и локального списков (как было) удалять НЕ УМЕЕТ: товар,
 * убранный на телефоне, приезжает обратно с компьютера, где он ещё лежит, и записывается на
 * сервер — удалить его становится нельзя в принципе, пока жив хоть один браузер со старой копией.
 * База отвечает на вопрос «этого товара нет, потому что его удалили, или потому что его тут
 * никогда не было»: пропал относительно базы на сервере — удалили там; пропал локально —
 * удалили здесь.
 *
 * Без базы (первый вход на устройстве) остаётся объединение: гость, накопивший список до входа,
 * должен получить его вместе со своим серверным, а не вместо.
 *
 * @param {WishlistItem[]|null} base
 * @param {WishlistItem[]} server
 * @param {WishlistItem[]} local
 * @returns {WishlistItem[]}
 */
function mergeWishlists(base, server, local) {
  const out = [];
  const seen = new Set();
  const push = (item) => {
    if (!item || seen.has(item.id)) return;
    seen.add(item.id);
    out.push(item);
  };

  if (!base) {
    for (const item of server) push(item);
    for (const item of local) push(item);
    return out;
  }

  const baseIds = new Set(base.map((i) => i.id));
  const serverIds = new Set(server.map((i) => i.id));
  const localIds = new Set(local.map((i) => i.id));
  const goneFromServer = (id) => baseIds.has(id) && !serverIds.has(id);
  const goneFromLocal = (id) => baseIds.has(id) && !localIds.has(id);

  // Свои записи первыми: при расхождении вариантов у одного товара побеждает то, что покупатель
  // видит прямо сейчас на этом устройстве.
  for (const item of local) if (!goneFromServer(item.id)) push(item);
  for (const item of server) if (!goneFromLocal(item.id)) push(item);
  return out;
}

/**
 * GA4 (через GTM): товар добавили в избранное — стандартное событие add_to_wishlist в формате
 * событий Stape (item_id — товар, item_variant — вариант). У сердечка есть только id и handle,
 * поэтому название и цену даёт /products/<handle>.js. Никогда не бросает.
 * @param {WishlistItem} item
 */
function reportWishlistAdd(item) {
  const root = window.Shopify?.routes?.root || '/';
  fetch(root + 'products/' + encodeURIComponent(item.handle) + '.js', { headers: { Accept: 'application/json' } })
    .then((res) => (res.ok ? res.json() : null))
    .catch(() => null)
    .then((product) => {
      const variants = (product && product.variants) || [];
      // Сердечко на странице товара сохраняет товар без варианта — тогда в событии вариант, выбранный на
      // странице, а вне её первый в наличии: тот, что человек видит на карточке.
      const onPage = document.querySelector(`product-form-component[data-product-id="${item.id}"] input[name="id"]`);
      const wanted = item.variantId || (onPage instanceof HTMLInputElement ? onPage.value : '');
      const variant =
        variants.find((v) => String(v.id) === String(wanted)) || variants.find((v) => v.available) || variants[0] || null;
      const price = variant ? variant.price / 100 : undefined;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'add_to_wishlist',
        ecommerce: {
          currency: window.Shopify?.currency?.active,
          value: price,
          items: [
            {
              item_id: item.id,
              item_sku: (variant && variant.sku) || undefined,
              item_variant: variant ? String(variant.id) : item.variantId || undefined,
              item_name: product ? product.title : undefined,
              variant_name: variant && variant.title !== 'Default Title' ? variant.title : undefined,
              item_category: (product && product.type) || undefined,
              item_brand: (product && product.vendor) || undefined,
              price,
              quantity: 1,
            },
          ],
        },
      });
    });
}

/**
 * @typedef {object} WishlistItem
 * @property {string} id - The product id.
 * @property {string} handle - The product handle (used to render cards on the wishlist page).
 * @property {string|null} [variantId] - The variant selected when saved (so the card shows it).
 */

class WishlistStore {
  /** @type {WishlistItem[]} */
  #items = [];
  /** @type {ReturnType<typeof setTimeout> | null} */
  #syncTimer = null;
  /** Есть ли неотправленное изменение. Без него выгрузка страницы слала бы POST каждый раз. */
  #syncPending = false;
  /** Прошла ли первая гидратация. До неё отправлять НЕЛЬЗЯ: воркер список замещает. */
  #hydrated = false;

  constructor() {
    this.#items = this.#read();
  }

  /**
   * Перечитывает список из localStorage. Стор читает хранилище ОДИН раз, в конструкторе, поэтому
   * страница, поднятая из bfcache, и вторая вкладка держат устаревший снимок — и отправили бы его
   * на сервер, стерев то, что человек добавил в соседней вкладке.
   */
  reloadFromStorage() {
    const stored = this.#read();
    if (JSON.stringify(stored) === JSON.stringify(this.#items)) return;
    this.#items = stored;
    this.#emit();
  }

  /** @returns {WishlistItem[]} A defensive copy of the stored items. */
  get items() {
    return this.#items.map((item) => ({ ...item }));
  }

  get count() {
    return this.#items.length;
  }

  /**
   * @param {string | number} id
   * @returns {boolean}
   */
  has(id) {
    const key = String(id);
    return this.#items.some((item) => item.id === key);
  }

  /**
   * @param {WishlistItem} item
   */
  add(item) {
    const normalized = this.#normalize(item);
    if (!normalized || this.has(normalized.id)) return;
    this.#items = [normalized, ...this.#items].slice(0, MAX_ITEMS);
    this.#commit();
    this.#emitEvent('add', normalized);
    reportWishlistAdd(normalized);
  }

  /**
   * @param {string | number} id
   */
  remove(id) {
    const key = String(id);
    const removed = this.#items.find((item) => item.id === key);
    const next = this.#items.filter((item) => item.id !== key);
    if (next.length === this.#items.length) return;
    this.#items = next;
    this.#commit();
    if (removed) this.#emitEvent('remove', removed);
  }

  /**
   * Logs a wishlist action to the analytics backend (D1) — first-party, server-side. Never throws.
   * @param {'add'|'remove'} type
   * @param {WishlistItem} item
   */
  #emitEvent(type, item) {
    const cfg = window.R69 && window.R69.wishlist;
    const base = cfg && typeof cfg.shareApi === 'string' ? cfg.shareApi.replace(/\/+$/, '') : '';
    if (!base || !item || !item.handle) return;
    try {
      fetch(base + '/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          handle: item.handle,
          // Stable across locales — this store translates handles per language, so analytics
          // aggregates by product id server-side.
          productId: item.id || null,
          variantId: item.variantId || null,
          customerId: cfg.customerId || null,
          guestId: cfg.customerId ? null : ownerId(),
          // Гостевой ключ шлём и вошедшему: он связывает то, что человек сохранял гостем,
          // с его покупками под аккаунтом. Новый ключ при этом не заводим.
          wid: (function () {
            try {
              return localStorage.getItem(OWNER_KEY) || null;
            } catch (error) {
              return null;
            }
          })(),
          locale: cfg.locale || '',
          // The store runs 3 domains x 2 languages — the host says WHERE the tap happened.
          host: location.host,
          shop: cfg.shop || '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {
      /* offline */
    }
  }

  /**
   * Сохраняет товар с этим вариантом — для кнопки «сообщить о поступлении» (assets/r69-notify.js).
   * Письмо «снова в наличии» воркер шлёт по СОХРАНЁННОМУ варианту, поэтому товар, лежащий в списке
   * с другим размером, переключается на нужный. Переключение — одно событие 'add': воркер берёт
   * последнее событие по товару, а пара remove + add могла бы прийти к нему в обратном порядке.
   * Без варианта (карточка распроданного товара) уже сохранённый вариант не трогаем.
   * @param {WishlistItem} item
   */
  save(item) {
    const normalized = this.#normalize(item);
    if (!normalized) return;
    const current = this.#items.find((i) => i.id === normalized.id);
    if (!current) {
      this.add(normalized);
      return;
    }
    if (!normalized.variantId || current.variantId === normalized.variantId) return;
    this.#items = this.#items.map((i) => (i.id === normalized.id ? normalized : i));
    this.#commit();
    this.#emitEvent('add', normalized);
  }

  /**
   * Toggles membership and returns the resulting state.
   * @param {WishlistItem} item
   * @returns {boolean} `true` if the item is now in the wishlist.
   */
  toggle(item) {
    const normalized = this.#normalize(item);
    if (!normalized) return false;
    if (this.has(normalized.id)) {
      this.remove(normalized.id);
      return false;
    }
    this.add(normalized);
    return true;
  }

  clear() {
    if (this.#items.length === 0) return;
    this.#items = [];
    this.#commit();
  }

  /**
   * Rewrites stored handles from an id -> handle map (locale normalization). Product handles on
   * this store are TRANSLATED per locale (ru/et/lt/lv all differ), and Liquid's `all_products`
   * only resolves the CURRENT locale's handle — so an item hearted on /ru/ is invisible to the
   * wishlist page rendered on /lv/. The wishlist page feeds an id->handle map for the current
   * locale from the cards it has just rendered, quietly — without a re-render.
   * @param {Record<string, string>} map - product id -> handle in the current locale.
   * @returns {boolean} whether anything changed.
   */
  rewriteHandles(map, options) {
    if (!map) return false;
    let changed = false;
    this.#items = this.#items.map((item) => {
      const handle = map[item.id];
      if (handle && handle !== item.handle) {
        changed = true;
        return { ...item, handle };
      }
      return item;
    });
    if (changed && options && options.quiet) this.#persist();
    else if (changed) this.#commit();
    return changed;
  }

  /**
   * Merges a server-provided list (from the customer metafield) into the local list.
   * Server entries win on order; local-only entries (added while logged out) are appended,
   * then the merged result is pushed back so every device converges on the same list.
   * @param {WishlistItem[]} serverItems
   */
  hydrate(serverItems, options) {
    if (!Array.isArray(serverItems)) return;
    // Авторитетный источник — только ответ /sync. Серверный метафилд (фолбэк в bootstrap) базу
    // не двигает и записью на сервер не считается.
    const authoritative = !options || options.authoritative !== false;
    const server = serverItems.map((item) => this.#normalize(item)).filter(Boolean);
    const baseDoc = authoritative ? readBase() : null;
    const rev = options && options.rev != null ? Number(options.rev) : null;
    // Ответ старее того, что мы уже записали, — это отставшая реплика KV, а не чужое удаление.
    // Применить его значило бы стереть только что добавленный товар на всех устройствах.
    if (authoritative && baseDoc && rev != null && rev < baseDoc.rev) {
      this.#hydrated = true;
      return;
    }
    const before = JSON.stringify(this.#items);
    const merged = mergeWishlists(baseDoc ? baseDoc.items : null, server, this.#items)
      .slice(0, MAX_ITEMS);
    this.#items = merged;
    // Сравнивать СОДЕРЖИМОЕ, а не длину: серверная запись может нести другой сохранённый вариант
    // того же товара. И объявлять только настоящее изменение — bootstrap гидратирует на каждой
    // загрузке страницы у вошедшего, безусловное событие заставляло секцию перерисовываться зря.
    const changed = JSON.stringify(merged) !== before;
    if (changed) {
      this.#persist();
      this.#emit();
    }
    if (!authoritative) return;
    this.#hydrated = true;
    // Сервер узнаёт о нашей стороне слияния только из POST. Совпало — фиксируем базу сразу.
    if (listKey(merged) === listKey(server)) {
      writeBase(rev == null ? (baseDoc ? baseDoc.rev : 0) : rev, server);
      this.#syncPending = false;
    } else {
      this.#sync();
    }
  }

  /**
   * @param {any} item
   * @returns {WishlistItem | null}
   */
  #normalize(item) {
    if (!item || typeof item !== 'object') return null;
    const id = item.id != null ? String(item.id) : '';
    const handle = item.handle != null ? String(item.handle) : '';
    if (!id || !handle) return null;
    const variantId = item.variantId != null && item.variantId !== '' ? String(item.variantId) : null;
    return variantId ? { id, handle, variantId } : { id, handle };
  }

  #commit() {
    this.#persist();
    this.#emit();
    this.#sync();
    markSeen(this.#items.length);
  }

  #persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#items));
    } catch (error) {
      /* Storage unavailable (e.g. private mode) — keep the in-memory list. */
    }
  }

  /** @returns {WishlistItem[]} */
  #read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => this.#normalize(item))
        .filter(/** @returns {item is WishlistItem} */ (item) => item != null);
    } catch (error) {
      return [];
    }
  }

  #emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { items: this.items, count: this.count },
      })
    );
  }

  /**
   * Reads the authoritative server list for a logged-in customer via the App Proxy.
   * Returns `null` (never throws) when sync is disabled, the customer is a guest, or the
   * proxy is unreachable — callers then fall back to the localStorage / Liquid-rendered list.
   * @returns {Promise<WishlistItem[] | null>}
   */
  async fetchRemote() {
    const config = window.R69?.wishlist;
    if (!config || !config.loggedIn || !config.syncEnabled) return null;
    try {
      const response = await fetch(config.endpoint || DEFAULT_ENDPOINT, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      const data = await response.json();
      // Гостю воркер отвечает `guest: true` — это не состояние аккаунта, применять его нельзя.
      if (!data || data.guest || !Array.isArray(data.items)) return null;
      return { items: data.items, rev: Number(data.rev) || 0 };
    } catch (error) {
      return null;
    }
  }

  /**
   * Persists to the server for logged-in customers via the App Proxy.
   * Debounced and failure-tolerant: until the companion app is deployed (or when offline),
   * this is a no-op and `localStorage` remains the source of truth.
   */
  #sync() {
    const config = window.R69?.wishlist;
    if (!config || !config.loggedIn || !config.syncEnabled) return;

    this.#syncPending = true;
    if (this.#syncTimer) clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(() => this.flushSync(), 600);
  }

  /**
   * Отправляет список НЕМЕДЛЕННО, отменяя отложенную отправку. Зовётся при уходе со страницы:
   * keepalive держит только УЖЕ выпущенный запрос, а отложенный на 600 мс таймер при выгрузке
   * просто не срабатывает — снятое сердечко терялось и товар возвращался на следующей загрузке.
   * Без неотправленных изменений не делает ничего.
   */
  flushSync() {
    if (!this.#syncPending) return;
    // Порядок обязателен: пока серверное состояние неизвестно, отправлять своё нельзя — воркер
    // список ЗАМЕЩАЕТ, и мы стёрли бы то, что человек добавил на другом устройстве. Изменение
    // никуда не денется: оно лежит в localStorage и уедет после гидратации или на следующий раз.
    if (!this.#hydrated) return;
    this.#syncPending = false;
    if (this.#syncTimer) {
      clearTimeout(this.#syncTimer);
      this.#syncTimer = null;
    }
    const config = window.R69?.wishlist;
    if (!config || !config.loggedIn || !config.syncEnabled) return;
    const snapshot = this.#items.slice(0, MAX_ITEMS);
    try {
      fetch(config.endpoint || DEFAULT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        // Личность кладём в тело: подпись прокси описывает ЖИВУЮ сессию, а список — тот, что
        // отрисован на этой странице. Человек мог выйти и войти другим в соседней вкладке, и
        // тогда список уехал бы в чужой аккаунт. Воркер сверяет и отвечает 409.
        body: JSON.stringify({ items: snapshot, customerId: config.customerId || null }),
        keepalive: true,
      })
        .then((response) => (response && response.ok ? response.json().catch(() => null) : null))
        .then((data) => {
          // База двигается только по подтверждённой записи: иначе неудачный POST выдал бы
          // неотправленное за синхронизированное и следующее слияние съело бы эти записи.
          if (data && data.ok) writeBase(data.rev, snapshot);
          else this.#syncPending = true;
        })
        .catch(() => {
          this.#syncPending = true;
        });
    } catch (error) {
      /* offline */
    }
  }
}

/**
 * Общий браузер: список НЕ принадлежит браузеру, он принадлежит человеку.
 *
 * localStorage один на весь домен и до сих пор ни к кому не был привязан. На семейном ноутбуке
 * это значило: покупатель A набрал список и вышел; сел B и вошёл в свой аккаунт — список A
 * приклеивался к аккаунту B, уезжал на все его устройства и удалить его было нечем. Публичная
 * ссылка «поделиться» тоже лежит в браузере, поэтому ссылка A начинала показывать список B.
 *
 * Правило асимметричное и намеренно. Гость, набравший список ДО входа, свой список сохраняет —
 * он для того и набирал, метку ставит только вошедший. Стираем РОВНО в одном случае: за браузером
 * сменился вошедший покупатель. Выход из аккаунта список не трогает: сессия Shopify истекает сама,
 * и человек не должен из-за этого увидеть пустой вишлист (а на трёх витринах он вообще залогинен
 * порознь). Защиту это сохраняет полностью: подмешать чужой список в аккаунт можно только войдя,
 * а вход с чужой меткой как раз и приводит к стиранию.
 *
 * Выполняется ДО создания стора, чтобы он не успел прочитать чужой список.
 */
(function claimLocalList() {
  try {
    const cfg = window.R69 && window.R69.wishlist;
    const owner = cfg && cfg.customerId ? 'c:' + cfg.customerId : '';
    const stored = localStorage.getItem(LIST_OWNER_KEY) || '';
    if (owner && stored.slice(0, 2) === 'c:' && stored !== owner) {
      // Ключи шаринга (OWNER_KEY / SHARE_KEY ниже по файлу) — секрет владельца и публичный код.
      // Публичная ссылка прежнего покупателя живёт на сервере: стереть ключ и уйти — значит
      // оставить её раздавать список, отозвать её после этого нечем.
      const wid = localStorage.getItem('r69:wid');
      const api = cfg && typeof cfg.shareApi === 'string' ? cfg.shareApi.replace(/\/+$/, '') : '';
      if (api && wid && localStorage.getItem('r69:share')) {
        fetch(api + '/share', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ownerId: wid }),
          keepalive: true,
        }).catch(() => {});
      }
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BASE_KEY);
      localStorage.removeItem('r69:wid');
      localStorage.removeItem('r69:share');
    }
    if (owner) localStorage.setItem(LIST_OWNER_KEY, owner);
  } catch (error) {
    /* приватный режим — хранить нечего, значит и чужого не осталось */
  }
})();

export const wishlist = new WishlistStore();

// Уход со страницы: дослать снятое сердечко, пока документ жив. pagehide ловит и переход по
// ссылке, и закрытие вкладки; visibilitychange — сворачивание на телефоне, где pagehide может
// не прийти вовсе.
window.addEventListener('pagehide', () => wishlist.flushSync());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') wishlist.flushSync();
});

// Страница из bfcache («назад») и соседняя вкладка: в памяти стора список, прочитанный при
// создании, и без перечитывания он уехал бы на сервер поверх более свежего.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) wishlist.reloadFromStorage();
});
window.addEventListener('storage', (event) => {
  if (!event.key || event.key === STORAGE_KEY) wishlist.reloadFromStorage();
});

window.R69 = window.R69 || {};
window.R69.wishlistStore = wishlist;

const SEEN_KEY = 'r69:wl-seen';
const CART_SENT_KEY = 'r69:wl-cart';

function markSeen(count) {
  if (!count) return;
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch (error) {
    /* private mode */
  }
}

function cartToken() {
  const m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
  if (!m) return '';
  let v = '';
  try {
    v = decodeURIComponent(m[1]);
  } catch (error) {
    return '';
  }
  v = v.split('?')[0];
  return /^[A-Za-z0-9_-]{10,64}$/.test(v) ? v : '';
}

/**
 * Связка «корзина ↔ человек». Заказ приходит к нам вебхуком, и в нём есть токен корзины, но НЕТ
 * того, кто складывал сердечки: 97% сохраняют гостями, а оформляют заказ уже под аккаунтом. Эта
 * отправка — единственный мост между ними, и только по нему покупка попадает в отчёт избранного.
 *
 * Поэтому отправляем при любом изменении корзины и на уходе со страницы, а не один раз за сессию:
 * токен рождается вместе с первой корзиной, и раньше мы успевали отчитаться далеко не всегда
 * (владелец 22.09.2026: «я хочу знать, сколько реально приносит вишлист»). Повтора одного и того
 * же не будет — метка в памяти вкладки хранит токен ВМЕСТЕ с человеком.
 */
function reportCart(маяком) {
  const cfg = window.R69 && window.R69.wishlist;
  const api = cfg && typeof cfg.shareApi === 'string' ? cfg.shareApi.replace(/\/+$/, '') : '';
  const token = cartToken();
  if (!api || !token) return;
  let wid = '';
  try {
    wid = localStorage.getItem(OWNER_KEY) || '';
  } catch (error) {
    /* private mode */
  }
  // человек нам известен, если он вошёл, уже пользовался избранным или держит в нём товар;
  // пустому гостю id не выдаём — иначе в базе заведутся люди, которые ничего не сохраняли
  const кто = cfg.customerId ? String(cfg.customerId) : '';
  if (!кто && !wid && wishlist.count === 0) return;
  const guestId = кто && !wid ? null : wid || ownerId();
  const метка = token + '|' + кто + '|' + (guestId || '');
  try {
    if (sessionStorage.getItem(CART_SENT_KEY) === метка) return;
    sessionStorage.setItem(CART_SENT_KEY, метка);
  } catch (error) {
    /* private mode — отправим ещё раз, беды в этом нет */
  }
  const тело = JSON.stringify({ token, customerId: кто || null, guestId });
  // уходя со страницы, обычный запрос браузер уже не отпустит — только маяком
  if (маяком && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(api + '/wl/cart', new Blob([тело], { type: 'application/json' }));
      return;
    } catch (error) {
      /* падать тут нельзя — ниже обычный путь */
    }
  }
  fetch(api + '/wl/cart', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: тело,
    keepalive: true,
  }).catch(() => {
    try {
      sessionStorage.removeItem(CART_SENT_KEY);
    } catch (error) {
      /* private mode */
    }
  });
}

(function clearCartAttributes() {
  const cfg = window.R69 && window.R69.wishlist;
  if (!cfg || !cfg.clearCartAttrs) return;
  const root = window.Shopify?.routes?.root || '/';
  fetch((root.endsWith('/') ? root : root + '/') + 'cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _r69_wishlist: '', _r69_pid: '' } }),
  }).catch(() => {});
})();

markSeen(wishlist.count);
reportCart();
// имя события берём у самой темы: 'cart:update' здесь не рассылает никто, и обработчик был мёртвый
document.addEventListener('shopify:cart:lines-update', () => reportCart());
document.addEventListener(CHANGE_EVENT, () => reportCart());
// уходит со страницы — последняя попытка маяком: корзина могла появиться только что
window.addEventListener('pagehide', () => reportCart(true));
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') reportCart(true);
});

// Hydrate a logged-in customer's wishlist so it follows them across devices.
// Prefer the authoritative App Proxy list; fall back to the server-rendered metafield.
(async function bootstrap() {
  const config = window.R69?.wishlist;
  if (!config?.loggedIn) return;

  if (config.syncEnabled) {
    const remote = await wishlist.fetchRemote();
    if (remote) {
      wishlist.hydrate(remote.items, { rev: remote.rev });
      return;
    }
  }

  if (Array.isArray(config.serverItems)) {
    wishlist.hydrate(config.serverItems, { authoritative: false });
  }
})();

// Импорта из Wishlist Guru здесь больше нет. Он был непрерывным: на КАЖДОЙ загрузке страницы
// читал localStorage `wg-local-list` и доливал новое, потому что Guru какое-то время ещё
// раздавался частью Rollout на прежней теме. Владелец 08.09.2026: переезд на свой вишлист
// давно состоялся, импорт сделан РАЗОВО, брать больше неоткуда — постоянный импортёр снят.
// Ключи `r69:wl-guru-seen` и `r69:wl-guru-imported` в браузерах остаются, они безвредны.

/* -------------------------------------------------------------------------------------------------
 * Wishlist SHARING — short, revocable, live links backed by the Cloudflare Worker (config.shareApi).
 * The browser holds a secret ownerId (write capability); a short public code maps to it server-side
 * and is never exposed back, so a recipient can READ the list but never write it. Once a code
 * exists, the live list is pushed to the worker on every change so the shared link stays current.
 * ---------------------------------------------------------------------------------------------- */
const OWNER_KEY = 'r69:wid';
const SHARE_KEY = 'r69:share';
const SHARE_PATH = '/pages/wishlist'; // the ROTAS69 wishlist page

function shareBase() {
  const url = window.R69?.wishlist?.shareApi || '';
  return typeof url === 'string' ? url.replace(/\/+$/, '') : '';
}

function ownerId() {
  let id = '';
  try {
    id = localStorage.getItem(OWNER_KEY) || '';
  } catch (error) {
    /* private mode */
  }
  if (!/^[A-Za-z0-9_-]{16,64}$/.test(id)) {
    const bytes = new Uint8Array(18);
    (window.crypto || globalThis.crypto)?.getRandomValues?.(bytes);
    id = 'w_' + Array.from(bytes, (b) => (b % 36).toString(36)).join('');
    try {
      localStorage.setItem(OWNER_KEY, id);
    } catch (error) {
      /* ignore */
    }
  }
  return id;
}

function readCode() {
  try {
    return localStorage.getItem(SHARE_KEY) || '';
  } catch (error) {
    return '';
  }
}
function writeCode(code) {
  try {
    if (code) localStorage.setItem(SHARE_KEY, code);
    else localStorage.removeItem(SHARE_KEY);
  } catch (error) {
    /* ignore */
  }
}

/** Calls the share backend. Returns the parsed JSON, or null when unconfigured/unreachable. */
async function shareApi(method, path, body) {
  const base = shareBase();
  if (!base) return null;
  try {
    const res = await fetch(base + path, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok && res.status !== 404) return null;
    return await res.json().catch(() => ({}));
  } catch (error) {
    return null;
  }
}

/** Locale-aware origin+path base for the shared link (keeps the buyer's language prefix). */
function shareLinkBase() {
  const root = window.Shopify?.routes?.root || '/';
  // root is '/', '/en-eu/', '/ru/', … — strip the trailing slash, keep the locale prefix.
  const prefix = root === '/' ? '' : root.replace(/\/+$/, '');
  return location.origin + prefix + SHARE_PATH;
}

const wishlistShare = {
  enabled() {
    return !!shareBase();
  },
  isOn() {
    return !!readCode();
  },
  code() {
    return readCode();
  },
  linkFor(code) {
    return shareLinkBase() + '?share=' + encodeURIComponent(code);
  },
  currentLink() {
    const code = readCode();
    return code ? this.linkFor(code) : '';
  },

  /** Reconcile the local code with the server (recovers button state across reloads/devices). */
  async refresh() {
    if (!this.enabled()) return readCode();
    const res = await shareApi('POST', '/share/status', { ownerId: ownerId() });
    if (res && 'code' in res) writeCode(res.code || '');
    return readCode();
  },

  /** Create OR rotate the code; pushes the current list so the link is live immediately. */
  async create() {
    if (!this.enabled()) return '';
    const res = await shareApi('POST', '/share', { ownerId: ownerId(), items: wishlist.items });
    if (res && res.code) writeCode(res.code);
    return readCode();
  },
  rotate() {
    return this.create();
  },

  /** Disable sharing — the link goes dead. */
  async disable() {
    if (this.enabled()) await shareApi('DELETE', '/share', { ownerId: ownerId() });
    writeCode('');
  },

  /** Push the live list to the worker (only meaningful once a code exists). */
  async pushList() {
    if (!this.enabled() || !readCode()) return;
    await shareApi('PUT', '/w/' + ownerId(), { items: wishlist.items });
  },
};

window.R69.wishlistShare = wishlistShare;
// Гостевой ключ нужен подписке «сообщить о поступлении» (assets/r69-notify.js): по нему воркер
// связывает почту с этим списком.
window.R69.wishlistOwnerId = ownerId;

// Keep the shared (live) list in sync once the owner has an active code.
let sharePushTimer = null;
document.addEventListener(CHANGE_EVENT, () => {
  if (!wishlistShare.enabled() || !wishlistShare.isOn()) return;
  if (sharePushTimer) clearTimeout(sharePushTimer);
  sharePushTimer = setTimeout(() => wishlistShare.pushList(), 700);
});

/**
 * Heart toggle button used on product cards and the product page.
 * Reads `data-product-id` and `data-product-handle` from the host element and
 * reflects membership through the `r69wl-on` class + `aria-pressed`.
 *
 * @extends {Component}
 */
class WishlistButton extends Component {
  /** @type {() => void} */
  #boundRender = () => this.#render();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#boundRender);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#boundRender);
  }

  /** @returns {WishlistItem} */
  get item() {
    // Вариант, который сохраняется: на карточке — выбранный свотч; на странице товара и в quick add —
    // вариант формы товара. Раньше бралась только карточка, и на странице товара товар сохранялся
    // без размера (владелец 31.10.2026: «вишлист должен сохранять выбранный вариант»).
    const card = this.closest('product-card');
    let variantId = null;
    if (card) {
      const checked = card.querySelector('input[type="radio"]:checked[data-variant-id]');
      variantId = checked instanceof HTMLInputElement ? checked.dataset.variantId || null : null;
    } else {
      const form =
        this.closest('product-form-component') ||
        document.querySelector(`product-form-component[data-product-id="${this.dataset.productId ?? ''}"]`);
      const input = form?.querySelector('input[name="id"]');
      variantId = input instanceof HTMLInputElement && input.value ? input.value : null;
    }

    return {
      id: this.dataset.productId ?? '',
      handle: this.dataset.productHandle ?? '',
      variantId,
    };
  }

  /**
   * Toggles membership. Bound via `on:click="/toggle"` on the inner button.
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();
    event.stopPropagation();

    const active = wishlist.toggle(this.item);
    this.#render(active);

    if (active) {
      this.classList.add('wishlist-button--bounce');
      setTimeout(() => this.classList.remove('wishlist-button--bounce'), 450);
    }
  }

  /**
   * @param {boolean} [active]
   */
  #render(active = wishlist.has(this.item.id)) {
    // Класс НЕ называется is-active намеренно. Имя класса — ключ инвалидации стиля в Blink, и
    // на этой теме два имени «отравлены» тем, что фигурируют в :has()-правилах: переключение
    // is-active или is-open на кнопке внутри product-card стоило 487 и 483 мс пересчёта на
    // /collections/auskari (10 730 элементов, CPU 4x), тогда как то же переключение уникального
    // имени — 0.40 мс, а нейтрального — 0.38 мс. Тот же класс на footer стоит 0.6 мс: дело именно
    // в имени, а не в элементе или в свойствах, которые оно меняет (те же display/fill инлайновым
    // стилем — 0.04 мс). Не переименовывать обратно и не переиспользовать здесь общие is-* имена.
    this.classList.toggle('r69wl-on', active);

    const button = this.querySelector('button');
    if (!(button instanceof HTMLButtonElement)) return;

    // Every wishlist-button on the page re-renders on each change event; write attributes only
    // when they differ, so the 20-40 untouched buttons on a grid stay silent instead of each
    // costing an attribute mutation (and the :has() re-match tax that follows any mutation).
    const pressed = String(active);
    if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Header counter bubble. Reuses the theme's `cart-bubble` markup/styling so it inherits the
 * header's adaptive colors (transparent vs solid). Because the count is client-side, any server
 * re-render of the header (e.g. adding to cart morphs the header) wipes it — so this extends
 * Component to get `updatedCallback` and also listens for `cart:update`, re-asserting the count.
 *
 * @extends {Component}
 */
class WishlistCount extends Component {
  /** @type {() => void} */
  #boundRender = () => this.#render();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#boundRender);
    document.addEventListener('cart:update', this.#boundRender);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#boundRender);
    document.removeEventListener('cart:update', this.#boundRender);
  }

  #render() {
    const count = wishlist.count;
    const label = count > 99 ? '99+' : String(count);

    this.toggleAttribute('hidden', count === 0);
    this.toggleAttribute('data-maintain-ratio', count <= 99);
    this.setAttribute('data-count', String(count));

    const target = this.querySelector('.cart-bubble__text-count') ?? this;
    target.textContent = label;

    const action = this.closest('.header-actions__wishlist');
    if (action) action.classList.toggle('has-count', count > 0);
  }
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

/**
 * Drives the wishlist page. The list lives in the browser: the script right after the section
 * builds the skeletons before the first paint, and the cards arrive in batches by id. On any
 * change it rebuilds the skeletons and the cards are fetched again.
 *
 * @typedef {object} WishlistListRefs
 * @property {HTMLElement} [grid] - The grid that holds the cards.
 * @property {HTMLElement} [empty] - The empty state.
 * @extends {Component<WishlistListRefs>}
 */
class WishlistList extends Component {
  /** @type {() => void} */
  #boundChange = () => this.#refresh();

  connectedCallback() {
    super.connectedCallback();

    // Shared wishlist: render those products READ-ONLY (never merged into the viewer's own list).
    // `?share=CODE` resolves to the owner's LIVE list via the share backend; `?w=h,h` is the
    // backend-free fallback (handles in the URL).
    const params = new URLSearchParams(location.search);
    const shareCode = params.get('share');
    const wParam = params.get('w');
    if (shareCode || wParam) {
      this.#renderShared(shareCode, wParam);
      return;
    }

    document.addEventListener(CHANGE_EVENT, this.#boundChange);
    if (Number(this.getAttribute('data-saved-count') || 0) !== wishlist.count) this.#refresh();
  }

  /**
   * Builds an id -> handle map for the CURRENT locale from the storefront catalog JSON.
   * Cached in sessionStorage per locale root; paginates only until the needed ids are found.
   * @param {string[]} neededIds
   * @returns {Promise<Record<string, string>>}
   */
  async #localeHandleMap(neededIds) {
    const root = window.Shopify?.routes?.root || '/';
    const base = root.endsWith('/') ? root : `${root}/`;
    const cacheKey = `r69:wlmap:${base}`;
    let map = {};
    try {
      map = JSON.parse(sessionStorage.getItem(cacheKey) || '{}') || {};
    } catch (error) {
      map = {};
    }
    if (neededIds.every((id) => map[id])) return map;

    for (let page = 1; page <= 5; page++) {
      let data;
      try {
        const res = await fetch(`${base}products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        data = await res.json();
      } catch (error) {
        break;
      }
      const products = Array.isArray(data?.products) ? data.products : [];
      for (const p of products) {
        if (p && p.id != null && p.handle) map[String(p.id)] = String(p.handle);
      }
      if (products.length < 250) break;
      if (neededIds.every((id) => map[id])) break;
    }
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(map));
    } catch (error) {
      /* storage full/unavailable — map still returned */
    }
    return map;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#boundChange);
  }

  #refresh() {
    window.R69WL?.shell?.(this, wishlist.count);
    document.dispatchEvent(new CustomEvent(RENDERED_EVENT));
  }

  /**
   * Renders a SHARED wishlist READ-ONLY — the shared pieces are never saved to the viewer's own
   * list. `code` resolves to the owner's LIVE list via the share backend; `w` is the in-URL handle
   * fallback. The shared handles and saved variants go onto this element and the skeletons are
   * rebuilt; the viewer's own list is untouched. Reveals the "shared" banner whose
   * "Save all" reads the rendered cards' ids to opt-in copy them.
   * @param {string|null} code
   * @param {string|null} w
   */
  async #renderShared(code, w) {
    let handles = [];
    const variants = {};
    let dead = false;

    if (code) {
      const base = (window.R69?.wishlist?.shareApi || '').replace(/\/+$/, '');
      if (base) {
        try {
          const res = await fetch(`${base}/share/${encodeURIComponent(code)}`);
          if (res.ok) {
            const data = await res.json().catch(() => ({}));
            const items = (Array.isArray(data.items) ? data.items : []).filter((i) => i && i.handle);
            for (const i of items) if (i.id != null && i.variantId) variants[String(i.id)] = String(i.variantId);
            // The sharer may browse another language — handles are per-locale here, so remap
            // shared ids to THIS locale's handles where possible (falls back to the shared handle).
            const ids = items.map((i) => (i.id != null ? String(i.id) : '')).filter(Boolean);
            const map = ids.length ? await this.#localeHandleMap(ids) : {};
            handles = items.map((i) => (i.id != null && map[String(i.id)]) || i.handle).filter(Boolean);
          } else {
            dead = true; // 404 → the owner regenerated or disabled the link
          }
        } catch (error) {
          dead = true;
        }
      } else {
        dead = true; // no backend configured to resolve a code
      }
    } else if (w) {
      try {
        handles = decodeURIComponent(w).split(',').map((h) => h.trim()).filter(Boolean);
      } catch (error) {
        handles = [];
      }
    }
    handles = handles.slice(0, 100);

    const banner = document.querySelector('[data-wl-shared-banner]');
    const saveBtn = banner && banner.querySelector('[data-wl-shared-save]');
    const setText = (msg) => {
      const t = banner && banner.querySelector('[data-wl-shared-text]');
      if (t) t.textContent = msg;
    };

    if (dead || !handles.length) {
      // Don't leave the viewer's OWN server-rendered list showing — that's the wrong list.
      this.innerHTML = '';
      if (banner) {
        banner.hidden = false;
        banner.setAttribute('data-state', 'dead');
        if (saveBtn) saveBtn.hidden = true;
        setText(dead ? banner.dataset.deadText || '' : banner.dataset.emptyText || '');
      }
      return;
    }

    this.dataset.renderHandles = handles.join(',');
    this.dataset.renderVariants = JSON.stringify(variants);
    window.R69WL?.shell?.(this, handles.length);
    document.dispatchEvent(new CustomEvent(RENDERED_EVENT));

    if (!banner) return;
    banner.hidden = false;
    if (saveBtn) {
      saveBtn.hidden = false;
      saveBtn.addEventListener('click', () => {
        for (const card of this.querySelectorAll('[data-wishlist-card]')) {
          if (!(card instanceof HTMLElement)) continue;
          wishlist.add({
            id: card.dataset.productId,
            handle: card.dataset.productHandle,
            variantId: card.dataset.savedVariantId || null,
          });
        }
        location.href = shareLinkBase();
      });
    }
  }

}

/**
 * The alerts block on the wishlist page: a guest leaves an email, a customer turns them on with one tap, and
 * either of them can turn them off there. The worker (App Proxy) makes the customer, subscribes them and
 * remembers whose wishlist it is.
 */
class WishlistAlerts {
  #box;
  #api;

  constructor(box) {
    this.#box = box;
    this.#api = box.dataset.endpoint || '';
    const form = box.querySelector('[data-wl-alerts-form]');
    const off = box.querySelector('[data-wl-alerts-off]');
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector('input[type="email"]');
      // from: which form subscribed — the app counts this page apart from "Notify me when available"
      this.#send({ action: 'on', email: input ? input.value.trim() : '', from: 'wishlist' }, form.querySelector('button'));
    });
    off?.addEventListener('click', () => this.#send({ action: 'off' }, off));
    this.#load();
  }

  async #load() {
    const state = await this.#call('GET');
    if (state) this.#show(state);
  }

  async #call(method, body) {
    const cfg = (window.R69 && window.R69.wishlist) || {};
    const url = this.#api + (method === 'GET' ? '?g=' + encodeURIComponent(ownerId()) : '');
    try {
      const res = await fetch(url, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body
          ? JSON.stringify({ ...body, guestId: ownerId(), market: cfg.market || '', locale: cfg.locale || '' })
          : undefined,
      });
      const data = await res.json().catch(() => null);
      return res.ok && data && !data.error ? data : null;
    } catch (error) {
      return null;
    }
  }

  async #send(body, button) {
    const err = this.#box.querySelector('[data-wl-alerts-err]');
    if (err) err.hidden = true;
    if (button) button.disabled = true;
    const state = await this.#call('POST', body);
    if (button) button.disabled = false;
    if (!state) {
      if (err) err.hidden = false;
      return;
    }
    this.#show(state);
  }

  /** @param {{on: boolean, email: string}} state */
  #show(state) {
    const on = this.#box.querySelector('[data-wl-alerts-on]');
    const form = this.#box.querySelector('[data-wl-alerts-form]');
    const text = this.#box.querySelector('[data-wl-alerts-on-text]');
    if (text) text.textContent = (this.#box.dataset.onText || '').replace('[email]', state.email || '');
    if (on) on.hidden = !state.on;
    if (form) form.hidden = !!state.on;
    this.#box.hidden = false;
  }
}

{
  const box = document.querySelector('[data-wl-alerts]');
  if (box instanceof HTMLElement) new WishlistAlerts(box);
}

if (!customElements.get('wishlist-list')) {
  customElements.define('wishlist-list', WishlistList);
}
